// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  GetApiDeliverablesApiArg,
  GetApiDeliverablesApiResponse,
  HandlersAttachmentListResponse,
  HandlersAttachmentResponse,
  HandlersProjectSummaryResponse,
  HandlersSignatureCreateRequest,
  HandlersSignatureResponse,
} from './athenaApiGen';
import type { RootState } from './store';
import { logout, setLoginToken } from './user/userStateSlice';
import { clearLocalStoragePreservingPreferences } from './user/logoutUtils';

// Base query configuration
const baseQuery = fetchBaseQuery({
  baseUrl: '/',
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).user.loginToken;
    // console.log('🔐 prepareHeaders - Token check:', {
    //   tokenExists: !!token,
    //   tokenPreview: token ? `${token.substring(0, 30)}...` : 'NONE',
    //   endpoint: headers.get('endpoint') || 'unknown'
    // });
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      return headers;
    } else {
      // console.warn('⚠️ No token found in Redux state!');
    }
  },
});

async function attemptTokenRefresh(): Promise<string | null> {
  try {
    const response = await fetch('/api/token', { credentials: 'include' });
    if (response.ok) {
      return await response.json(); // Returns JWT string
    }
    return null;
  } catch {
    return null;
  }
}

let refreshPromise: Promise<string | null> | null = null;

// Wrapper to handle 401 responses
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Avoid concurrent refresh attempts
    if (!refreshPromise) {
      refreshPromise = attemptTokenRefresh().finally(() => {
        refreshPromise = null;
      });
    }
    const newToken = await refreshPromise;
    if (newToken) {
      api.dispatch(setLoginToken(newToken));
      // Retry original request (new token will be picked up by prepareHeaders)
      result = await baseQuery(args, api, extraOptions);
    } else {
      // SAML session expired — full logout (preserve user preferences)
      api.dispatch(logout());
      clearLocalStoragePreservingPreferences();
      sessionStorage.clear();
      document.cookie =
        'login_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      const params = new URLSearchParams(window.location.search);
      const loggedOut = params.get('logged_out') === '1';
      if (!(window.location.pathname === '/sign-in' && loggedOut)) {
        window.location.href = loggedOut ? '/sign-in?logged_out=1' : '/sign-in';
      }
    }
  }

  return result;
};

// Extract a download filename from a Content-Disposition response header,
// preferring the RFC 5987 `filename*` form (handles non-ASCII) over the plain
// `filename`. Used to name blobs streamed from the API download endpoints.
const filenameFromContentDisposition = (
  header: string | null | undefined,
): string | undefined => {
  if (!header) return undefined;
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].replace(/^"|"$/g, '').trim());
    } catch {
      // malformed percent-encoding — fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim();
};

// Save a Blob to disk via a transient same-origin object URL + anchor click.
// The `download` attribute is honored here because blob: URLs are same-origin
// (unlike a cross-origin signed storage URL, where browsers ignore it).
const saveBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const athenaApi = createApi({
  baseQuery: baseQueryWithReauth,
  reducerPath: 'athenaApi',
  tagTypes: [
    'Attachment',
    'Deliverables',
    'Approval Queue',
    'NotificationPreferences',
    'FormVisibility',
  ],
  endpoints: (builder) => ({
    createProjectWithAttachments: builder.mutation({
      query: ({ projectData, attachments }) => {
        const formData = new FormData();

        // Append project data as JSON
        formData.append('title', projectData.title);
        formData.append('projectCode', projectData.projectCode);
        formData.append('programId', projectData.programId);
        formData.append('ownerUserId', projectData.ownerUserId);
        if (projectData.description) {
          formData.append('description', projectData.description);
        }
        if (projectData.projectFilePath) {
          formData.append('projectFilePath', projectData.projectFilePath);
        }

        // Append file attachments
        if (attachments && attachments.length > 0) {
          attachments.forEach((file: File) => {
            formData.append('attachments', file);
          });
        }

        return {
          url: 'api/projects',
          method: 'POST',
          body: formData,
          // Don't set Content-Type header - browser will set it with boundary
        };
      },
    }),
    downloadAttachment: builder.mutation<{ filename?: string }, string>({
      // Services 0.27.2 (#1614) changed /api/attachments/{id}/download to stream
      // the file body same-origin (200 + Content-Disposition) instead of
      // 302-redirecting to a signed blob URL. The old redirect failed silently
      // on GCP buckets that don't return CORS headers (the "200 but no file"
      // symptom in Staging/Prod), and its cross-origin <a download> was ignored
      // by browsers regardless. Fetch the stream as a Blob through the
      // authenticated base query and save it client-side — same-origin on every
      // storage provider, and a real error (not a silent no-op) when it fails.
      queryFn: async (id, _api, _extra, fetchWithBQ) => {
        const result = await fetchWithBQ({
          url: `api/attachments/${id}/download`,
          responseHandler: (response) => response.blob(),
          cache: 'no-cache',
        });
        if (result.error) return { error: result.error };
        const blob = result.data as Blob;
        const meta = result.meta as FetchBaseQueryMeta | undefined;
        const filename =
          filenameFromContentDisposition(
            meta?.response?.headers.get('content-disposition'),
          ) ?? 'download';
        saveBlob(blob, filename);
        return { data: { filename } };
      },
    }),
    previewAttachment: builder.mutation<
      { url: string; mimeType: string },
      string
    >({
      query: (id) => ({
        url: `api/attachments/${id}/download`,
        responseHandler: async (response) => {
          if (!response.ok) {
            throw new Error('Server error');
          }
          return response.blob();
        },
        cache: 'no-cache',
      }),
      transformResponse: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        return { url, mimeType: blob.type };
      },
    }),
    downloadZip: builder.mutation({
      query: ({ ids }) => ({
        url: `api/attachments/download`,
        method: 'POST',
        body: {
          attachmentIds: ids,
        },
        responseHandler: async (response) => {
          if (!response.ok) {
            throw new Error('Server error');
          }
          // The handler returns the blob itself, but it won't be cached
          return response.blob();
        },
        cache: 'no-cache', // Prevent the result from being stored
      }),
      transformResponse: (blob: Blob, _meta, arg) => {
        // We handle the download here, after the blob is received.
        // Guarantee a `.zip` extension: the HTML `download` attribute is taken
        // literally, so a title containing a dot (e.g. "Deliverable.v1") would
        // otherwise make the browser treat the trailing segment as the
        // extension and save an unusable file.
        const ensureZip = (name?: string) => {
          const base = name?.trim() || 'athena_attachments';
          return /\.zip$/i.test(base) ? base : `${base}.zip`;
        };
        saveBlob(blob, ensureZip(arg.fileName));
      },
    }),
    downloadLibraryZip: builder.mutation<{ filename?: string }, string>({
      // GET /api/library/{id}/download streams a same-origin ZIP of a folder's
      // or project's contents (200 + Content-Disposition), built server-side by
      // DownloadLibrary. Fetch it as a Blob through the authenticated base query
      // and save it client-side — same pattern as downloadAttachment. A folder /
      // project with no downloadable files returns 404, surfaced to the caller
      // as a normal mutation error.
      queryFn: async (id, _api, _extra, fetchWithBQ) => {
        const result = await fetchWithBQ({
          url: `api/library/${id}/download`,
          responseHandler: (response) => response.blob(),
          cache: 'no-cache',
        });
        if (result.error) return { error: result.error };
        const blob = result.data as Blob;
        const meta = result.meta as FetchBaseQueryMeta | undefined;
        const filename =
          filenameFromContentDisposition(
            meta?.response?.headers.get('content-disposition'),
          ) ?? 'download.zip';
        saveBlob(blob, filename);
        return { data: { filename } };
      },
    }),
    getDeliverables: builder.query<
      GetApiDeliverablesApiResponse,
      GetApiDeliverablesApiArg
    >({
      query: (args) => {
        const {
          workflowState,
          page,
          limit,
          projectId,
          deliverableTypeId,
          authorId,
        } = args || {};
        const params = new URLSearchParams();
        if (page) {
          params.append('page', page.toString());
        }
        if (limit) {
          params.append('limit', limit.toString());
        }
        if (projectId) {
          params.append('projectId', projectId);
        }
        if (deliverableTypeId) {
          params.append('deliverableTypeId', deliverableTypeId);
        }
        if (authorId) {
          params.append('authorId', authorId);
        }

        workflowState?.forEach((workflow: any) =>
          params.append('workflowState', workflow),
        );
        return {
          url: 'api/deliverables',
          params,
          method: 'GET',
        };
      },
      providesTags: ['Deliverables'],
    }),

    // Sitewide Settings — moved to per-program routes on services-0.15.1.
    // The frontend now uses the codegen hooks directly:
    //   useGetApiProgramsByIdSiteSettingsQuery
    //   usePutApiProgramsByIdSiteSettingsAdminEmailMutation
    //   useGetApiProgramsByIdSiteSettingsDomainsQuery
    //   usePostApiProgramsByIdSiteSettingsDomainsMutation
    //   useDeleteApiProgramsByIdSiteSettingsDomainsAndDomainMutation
    // The previous unscoped /api/site-settings/* hooks were removed (DRIFT-008).

    // Form Visibility endpoints (backend route: /api/platform-config/forms)
    getFormVisibility: builder.query<FormVisibilityResponse, void>({
      query: () => ({ url: 'api/platform-config/forms', method: 'GET' }),
      providesTags: ['FormVisibility'],
    }),
    updateFormVisibility: builder.mutation<
      FormVisibilityResponse,
      UpdateFormVisibilityRequest
    >({
      query: (body) => ({
        url: 'api/platform-config/forms',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FormVisibility'],
    }),

    // Workflow signing — POST /api/signatures.
    //
    // Codegen produces a `postSignatures` mutation that calls `/signatures`
    // because the backend swagger annotation (signature_handlers.go:78)
    // omits the `/api` prefix. The actual chi route is mounted under
    // `/api/signatures` (routes.go:591 inside `r.Route("/api", ...)`).
    // Until that annotation is corrected upstream, this hand-rolled
    // mutation is the working entry point and should be used by callers.
    createSignature: builder.mutation<
      HandlersSignatureResponse,
      HandlersSignatureCreateRequest
    >({
      query: (body) => ({
        url: 'api/signatures',
        method: 'POST',
        body,
      }),
    }),
    getSignatureById: builder.query<HandlersSignatureResponse, { id: string }>({
      query: ({ id }) => ({
        url: `api/signatures/${id}`,
        method: 'GET',
      }),
    }),

    // TODO: Remove this override once backend adds attachments to GET /api/projects/{id}/summary
    getProjectSummaryWithAttachments: builder.query<
      HandlersProjectSummaryResponse & {
        attachments?: HandlersAttachmentResponse[];
      },
      { id: string }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const [summaryResult, attachmentsResult] = await Promise.all([
          fetchWithBQ(`/api/projects/${arg.id}/summary`),
          fetchWithBQ(`/api/attachments?objectId=${arg.id}&objectType=Project`),
        ]);

        if (summaryResult.error)
          return { error: summaryResult.error as FetchBaseQueryError };

        const summary = summaryResult.data as HandlersProjectSummaryResponse;
        const attachmentsList = attachmentsResult.data as
          | HandlersAttachmentListResponse
          | undefined;

        return {
          data: {
            ...summary,
            attachments: attachmentsList?.attachments ?? [],
          },
        };
      },
      providesTags: ['Deliverables', 'Attachment'],
    }),
  }),
});

// Types for platform configuration
export interface FormVisibilityEntry {
  formType: string;
  objectType?: string;
  description?: string;
  enabled: boolean;
}

export interface FormVisibilityResponse {
  forms: FormVisibilityEntry[];
}

export interface UpdateFormVisibilityEntry {
  formType: string;
  enabled: boolean;
}

export interface UpdateFormVisibilityRequest {
  forms: UpdateFormVisibilityEntry[];
}

export const {
  useCreateProjectWithAttachmentsMutation,
  useDownloadAttachmentMutation,
  usePreviewAttachmentMutation,
  useGetDeliverablesQuery,
  useLazyGetDeliverablesQuery,
  useDownloadZipMutation,
  useDownloadLibraryZipMutation,
  useGetFormVisibilityQuery,
  useUpdateFormVisibilityMutation,
  useGetProjectSummaryWithAttachmentsQuery,
  useLazyGetProjectSummaryWithAttachmentsQuery,
  useCreateSignatureMutation,
  useGetSignatureByIdQuery,
  useLazyGetSignatureByIdQuery,
} = athenaApi;
